import React, { useCallback, useMemo } from 'react';
import { ReduxState } from '../../../../../../../../../store';
import { useSelector } from 'react-redux';
import {
  InstructionType,
  RedistributionImportInputData,
} from '../../../../redistributionImportTypes';
import { ColumnsType } from 'antd/es/table';
import ScrolledTableWrapper from '../../../../../../../../../components/global/ScrolledTableWrapper/ScrolledTableWrapper';
import { ImportedItemRow } from '../../../../../../../../../lib/imports/importTypes';
import { DataTable } from '@ydistri/ds';

const virtualTableScroll = { y: 600, x: 600 };

interface RedistributionImportParsedItemsTableProps {
  items: ImportedItemRow<RedistributionImportInputData>[];
}

/**
 * Displays parsed items from the redistribution import in a table format.
 * Visual differentiation is applied to rows with errors, and totals are displayed above the table.
 * Clicking on the stat items filters the table to show only valid or error items.
 */
const RedistributionImportParsedItemsTable: React.FC<RedistributionImportParsedItemsTableProps> = ({
  items,
}) => {
  const instructionSpecification = useSelector(
    (state: ReduxState) => state.calculations.redistributionImport.instructionSpecification,
  );

  const tableColumns = useMemo((): ColumnsType<ImportedItemRow<RedistributionImportInputData>> => {
    const result: ColumnsType<ImportedItemRow<RedistributionImportInputData>> = [
      {
        key: 'rowNumber',
        title: 'Row',
        dataIndex: 'rowNumber',
        align: 'center',
        width: 60,
      },
      {
        key: 'productIdentifier',
        title: 'Product Identifier',
        dataIndex: ['inputData', 'productIdentifier'],
      },
      {
        key: 'sourceStoreIdentifier',
        title: 'Source Store',
        dataIndex: ['inputData', 'sourceStoreIdentifier'],
      },
      {
        key: 'targetStoreIdentifier',
        title: 'Target Store',
        dataIndex: ['inputData', 'targetStoreIdentifier'],
      },
    ];

    if (
      instructionSpecification === InstructionType.QUANTITY ||
      instructionSpecification === InstructionType.QUANTITY_DESCRIPTION
    ) {
      result.push({
        key: 'quantity',
        title: 'Quantity',
        dataIndex: ['inputData', 'count'],
        align: 'right',
        width: 90,
      });
    }

    if (
      instructionSpecification === InstructionType.DESCRIPTION ||
      instructionSpecification === InstructionType.QUANTITY_DESCRIPTION
    ) {
      result.push({
        key: 'description',
        title: 'Description',
        dataIndex: ['inputData', 'description'],
        width: 200,
      });
    }

    result.push({
      key: 'errors',
      title: 'Errors and notes',
      render: (record: ImportedItemRow<RedistributionImportInputData>) =>
        [...(record.errors ?? []), ...(record.warnings ?? [])].join(', '),
      width: 390,
    });

    return result;
  }, [instructionSpecification]);

  const rowClassProvider = useCallback((record: ImportedItemRow<RedistributionImportInputData>) => {
    if (record.isError) {
      return 'red';
    } else {
      return 'green';
    }
  }, []);

  const rowKeyProvider = useCallback(
    (row: ImportedItemRow<RedistributionImportInputData>) => row.rowNumber,
    [],
  );

  return (
    <ScrolledTableWrapper offset={20}>
      <DataTable<ImportedItemRow<RedistributionImportInputData>>
        id="RedistributionImportParsedItems"
        columns={tableColumns}
        dataSource={items}
        showHeader
        showBorder
        flexGrow={1}
        rowClassName={rowClassProvider}
        rowKey={rowKeyProvider}
        virtual
        scroll={virtualTableScroll}
      />
    </ScrolledTableWrapper>
  );
};

export default RedistributionImportParsedItemsTable;
